import React, { forwardRef } from 'react';
import Icon, { IconName } from './icons/Icon';
import './InputNumber.css';

type InputNumberVariant = 'default' | 'ghost';

interface InputNumberProps {
  variant?: InputNumberVariant;
  hasLeftIcon?: boolean;
  leftIcon?: IconName;
  placeholder?: string;
  value?: number | undefined;
  defaultValue?: number | undefined;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  min?: number;
  max?: number;
  step?: number | 'any';
  /** ブラウザのスピンボタンを非表示にする（既定: true） */
  hideSpinner?: boolean;
  /** ルート要素の横幅（例: 120, '120px', '8rem', '100%'） */
  width?: number | string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(({
  variant = 'default',
  hasLeftIcon = false,
  leftIcon = 'inventory',
  placeholder,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  error = false,
  required = false,
  name,
  id,
  min,
  max,
  step = 1,
  hideSpinner = true,
  width,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) => {
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;
  return (
    <div
      className={`input-number input-number--${variant} ${error ? 'input-number--error' : ''} ${disabled ? 'input-number--disabled' : ''} ${hideSpinner ? 'input-number--no-spinner' : ''} ${className}`}
      style={resolvedWidth ? { width: resolvedWidth } : undefined}
    >
      <div className="input-number__state-layer">
        <div className="input-number__container">
          {hasLeftIcon && (
            <div className="input-number__icon">
              <Icon name={leftIcon} size={24} />
            </div>
          )}
          <input
            ref={ref}
            className="input-number__input text-single-large"
            type="number"
            placeholder={placeholder}
            value={value ?? ''}
            defaultValue={defaultValue}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            name={name}
            id={id}
            min={min}
            max={max}
            step={step}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error}
            inputMode="numeric"
          />
        </div>
      </div>
    </div>
  );
});

InputNumber.displayName = 'InputNumber';

export default InputNumber;
